import { SITE_LANGUAGES } from './localeShared';

export const BANNER_LOCALE_CODES = SITE_LANGUAGES.map((language) => language.i18nextCode);

const BANNER_LOCALE_CODE_SET = new Set(BANNER_LOCALE_CODES);
const BANNER_LOCALE_SORT_ORDER = new Map(BANNER_LOCALE_CODES.map((code, index) => [code, index]));

function normalizeRawLocaleValues(rawLocales: unknown): string[] {
  if (rawLocales == null) {
    return [];
  }

  if (Array.isArray(rawLocales)) {
    return rawLocales.flatMap((locale) => normalizeRawLocaleValues(locale));
  }

  if (typeof rawLocales !== 'string') {
    throw new Error('Banner locales must be submitted as locale codes');
  }

  return [rawLocales];
}

function sortBannerLocaleCodes(a: string, b: string): number {
  return (
    (BANNER_LOCALE_SORT_ORDER.get(a) ?? Number.MAX_SAFE_INTEGER) -
    (BANNER_LOCALE_SORT_ORDER.get(b) ?? Number.MAX_SAFE_INTEGER)
  );
}

export function normalizeBannerLocales(rawLocales: unknown): string[] {
  const normalizedLocales = normalizeRawLocaleValues(rawLocales)
    .map((locale) => locale.trim().toLowerCase())
    .filter((locale) => locale.length > 0);

  const uniqueLocales = Array.from(new Set(normalizedLocales));
  const invalidLocales = uniqueLocales.filter((locale) => !BANNER_LOCALE_CODE_SET.has(locale));
  if (invalidLocales.length > 0) {
    throw new Error(`Unknown banner locale code: ${invalidLocales.join(', ')}`);
  }

  if (uniqueLocales.length === BANNER_LOCALE_CODES.length) {
    return [];
  }

  return uniqueLocales.sort(sortBannerLocaleCodes);
}

export function bannerLocalesEqual(left: unknown, right: unknown): boolean {
  return normalizeBannerLocales(left).join('|') === normalizeBannerLocales(right).join('|');
}

export function bannerTargetsCurrentLocale(rawLocales: unknown, currentLocale: string): boolean {
  const bannerLocales = normalizeBannerLocales(rawLocales);
  if (bannerLocales.length === 0) {
    return true;
  }

  const [normalizedCurrentLocale] = normalizeBannerLocales(currentLocale);
  return bannerLocales.includes(normalizedCurrentLocale);
}
